(() => {
  'use strict';

  const state = {
    modalScrollY: 0,
    initializedSections: new WeakSet(),
  };

  const qs = (selector, context = document) => context.querySelector(selector);
  const qsa = (selector, context = document) => Array.from(context.querySelectorAll(selector));

  function initHeader(root = document) {
    qsa('[data-bodhi-header]', root).forEach((header) => {
      if (state.initializedSections.has(header)) return;
      state.initializedSections.add(header);

      const shell = qs('[data-bodhi-header-shell]', header);
      const drawer = qs('[data-bodhi-drawer]', document);
      const openButton = qs('[data-bodhi-drawer-open]', header);
      const closeButtons = drawer ? qsa('[data-bodhi-drawer-close]', drawer) : [];

      const closeDrawer = () => {
        if (!drawer) return;
        drawer.classList.remove('is-open');
        drawer.setAttribute('aria-hidden', 'true');
        openButton?.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('bodhi-menu-open');
      };

      const openDrawer = () => {
        if (!drawer) return;
        drawer.classList.add('is-open');
        drawer.setAttribute('aria-hidden', 'false');
        openButton?.setAttribute('aria-expanded', 'true');
        document.body.classList.add('bodhi-menu-open');
        requestAnimationFrame(() => qs('a, button, summary', drawer)?.focus());
      };

      openButton?.addEventListener('click', openDrawer);
      closeButtons.forEach((button) => button.addEventListener('click', closeDrawer));

      drawer?.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') closeDrawer();
      });

      if (shell?.classList.contains('is-sticky')) {
        const updateScrolled = () => shell.classList.toggle('is-scrolled', window.scrollY > 8);
        updateScrolled();
        window.addEventListener('scroll', updateScrolled, { passive: true });
      }
    });
  }

  function initRotators(root = document) {
    qsa('[data-bodhi-rotator]', root).forEach((rotator) => {
      if (rotator.dataset.initialized === 'true') return;
      rotator.dataset.initialized = 'true';

      const target = qs('[data-bodhi-rotator-word]', rotator);
      if (!target) return;

      let words = [];
      try {
        words = JSON.parse(rotator.dataset.words || '[]');
      } catch (error) {
        words = [];
      }
      words = words.filter(Boolean);
      if (words.length < 2) return;

      const interval = Math.max(Number(rotator.dataset.interval) || 2400, 900);
      let index = 0;
      let timer;

      const updateWord = () => {
        index = (index + 1) % words.length;
        target.classList.remove('is-changing');
        void target.offsetWidth;
        target.textContent = words[index];
        target.classList.add('is-changing');
      };

      const start = () => {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        timer = window.setInterval(updateWord, interval);
      };

      const stop = () => {
        if (timer) window.clearInterval(timer);
      };

      rotator.addEventListener('mouseenter', stop);
      rotator.addEventListener('mouseleave', start);
      start();
    });
  }

  function initIngredientModals(root = document) {
    qsa('[data-bodhi-ingredients-section]', root).forEach((section) => {
      if (section.dataset.initialized === 'true') return;
      section.dataset.initialized = 'true';

      const modal = qs('[data-bodhi-ingredient-modal]', section);
      if (!modal) return;

      const panel = qs('[data-bodhi-modal-panel]', modal);
      const closeButtons = qsa('[data-bodhi-modal-close]', modal);
      const title = qs('[data-bodhi-modal-title]', modal);
      const image = qs('[data-bodhi-modal-image]', modal);
      const stage = qs('[data-bodhi-modal-stage]', modal);
      const dose = qs('[data-bodhi-modal-dose]', modal);
      const role = qs('[data-bodhi-modal-role]', modal);
      const summary = qs('[data-bodhi-modal-summary]', modal);
      const detail = qs('[data-bodhi-modal-detail]', modal);
      const link = qs('[data-bodhi-modal-link]', modal);
      let opener = null;

      const open = (button) => {
        opener = button;
        state.modalScrollY = window.scrollY;

        const data = button.dataset;
        if (title) title.textContent = data.title || '';
        if (stage) stage.textContent = data.stage || '';
        if (dose) dose.textContent = data.dose || '';
        if (role) role.textContent = data.role || '';
        if (summary) summary.textContent = data.summary || '';
        if (detail) detail.textContent = data.detail || '';
        if (link) {
          link.href = data.link || '#';
          link.hidden = !data.link;
        }
        if (image) {
          image.src = data.image || '';
          image.alt = data.title || '';
        }

        modal.classList.add('is-open');
        modal.setAttribute('aria-hidden', 'false');
        document.body.classList.add('bodhi-modal-open');
        document.body.style.top = `-${state.modalScrollY}px`;
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';

        requestAnimationFrame(() => qs('[data-bodhi-modal-close]', modal)?.focus());
      };

      const close = () => {
        modal.classList.remove('is-open');
        modal.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('bodhi-modal-open');
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, state.modalScrollY);
        window.setTimeout(() => opener?.focus(), 0);
      };

      qsa('[data-bodhi-ingredient-open]', section).forEach((button) => {
        button.addEventListener('click', () => open(button));
      });

      closeButtons.forEach((button) => button.addEventListener('click', close));

      modal.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') close();
        if (event.key !== 'Tab' || !panel) return;

        const focusable = qsa(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
          panel,
        ).filter((element) => !element.hidden);

        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
    });
  }

  function initReveal(root = document) {
    const items = qsa('[data-bodhi-reveal]', root).filter((item) => item.dataset.revealInitialized !== 'true');
    if (!items.length) return;

    items.forEach((item) => {
      item.dataset.revealInitialized = 'true';
      item.classList.add('bodhi-reveal');
    });

    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.15 },
    );

    items.forEach((item) => observer.observe(item));
  }

  function initExpectJourney(root = document) {
    qsa('[data-bodhi-expect-journey]', root).forEach((journey) => {
      if (journey.dataset.expectInitialized === 'true') return;
      journey.dataset.expectInitialized = 'true';

      if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        journey.classList.add('is-visible');
        return;
      }

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) return;
          journey.classList.add('is-visible');
          observer.disconnect();
        },
        { threshold: 0.25 },
      );

      observer.observe(journey);
    });
  }

  function initAll(root = document) {
    initHeader(root);
    initRotators(root);
    initIngredientModals(root);
    initReveal(root);
    initExpectJourney(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:reorder', () => initAll(document));
})();
